import { Fragment, useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import {
  ApiError,
  fetchAdminAffiliates,
  fetchAdminAffiliatePayments,
  recordAdminAffiliatePayment,
  type Affiliate,
  type AffiliatePaymentsSummary,
} from '../lib/api';
import { formatCurrency, formatDateTime, formatPrimaryCurrency } from '../lib/format';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const ADMIN_NAV = [
  { to: '/admin', label: 'Visão geral' },
  { to: '/admin/afiliados', label: 'Afiliados' },
];

interface PaymentFormState {
  amount: string;
  currency: string;
  paidAt: string;
  note: string;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function emptyForm(): PaymentFormState {
  return { amount: '', currency: 'BRL', paidAt: todayIsoDate(), note: '' };
}

// Só leitura em relação ao CADASTRO do afiliado (nome/e-mail/cupom — isso
// continua sendo só a planilha do Google). "A receber"/"Pago" e o
// histórico de pagamentos, por outro lado, são deste sistema — o admin
// registra um pagamento aqui, o afiliado vê refletido no dashboard dele
// (ver InfluencerDashboardPage.tsx).
export function AdminAffiliatesPage() {
  const { signOut } = useAdminAuth();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [paymentsByEmail, setPaymentsByEmail] = useState<Record<string, AffiliatePaymentsSummary>>({});
  const [openFormEmail, setOpenFormEmail] = useState<string | null>(null);
  const [form, setForm] = useState<PaymentFormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function loadPaymentsFor(email: string) {
    fetchAdminAffiliatePayments(email)
      .then((result) => {
        setPaymentsByEmail((prev) => ({ ...prev, [email]: result }));
      })
      .catch(() => {
        // Falha ao buscar "a receber" de UM afiliado não deve travar a
        // lista inteira — a linha dele simplesmente mostra 'N/D'.
      });
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAdminAffiliates()
      .then((result) => {
        if (cancelled) return;
        setAffiliates(result);
        result.forEach((affiliate) => loadPaymentsFor(affiliate.email));
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Erro ao carregar afiliados.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function toggleForm(email: string) {
    if (openFormEmail === email) {
      setOpenFormEmail(null);
      return;
    }
    setOpenFormEmail(email);
    setForm(emptyForm());
    setFormError(null);
  }

  async function handleSubmitPayment(email: string) {
    const amountNumber = Number(form.amount.replace(',', '.'));
    if (!Number.isFinite(amountNumber) || amountNumber <= 0) {
      setFormError('Valor precisa ser um número maior que zero.');
      return;
    }

    setSubmitting(true);
    setFormError(null);
    try {
      await recordAdminAffiliatePayment({
        affiliateEmail: email,
        amount: amountNumber,
        currency: form.currency,
        paidAt: new Date(`${form.paidAt}T12:00:00`).toISOString(),
        note: form.note || undefined,
      });
      loadPaymentsFor(email);
      setOpenFormEmail(null);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Não foi possível registrar o pagamento.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Layout nav={ADMIN_NAV} onSignOut={() => signOut()}>
      <h1 className="font-display text-2xl font-bold tracking-tight">Afiliados</h1>
      <p className="mt-1 text-sm text-[rgb(var(--text-muted))]">
        Lista de influenciadores cadastrados na planilha. Para adicionar, remover ou alterar nome/e-mail/cupom, edite a
        planilha diretamente. "A receber" e "Pago" são registrados por aqui, sempre que você fizer um pagamento.
      </p>

      {error ? <p className="mt-4 text-sm text-error">{error}</p> : null}

      <div className="card mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[rgb(var(--border))] text-left text-xs uppercase tracking-wide text-[rgb(var(--text-muted))]">
              <th className="px-4 py-3 font-medium">Nome</th>
              <th className="px-4 py-3 font-medium">E-mail</th>
              <th className="px-4 py-3 font-medium">Cupons</th>
              <th className="px-4 py-3 font-medium">Pix</th>
              <th className="px-4 py-3 font-medium text-right">A receber</th>
              <th className="px-4 py-3 font-medium text-right">Pago</th>
              <th className="px-4 py-3 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {affiliates.map((affiliate) => {
              const payments = paymentsByEmail[affiliate.email];
              const formOpen = openFormEmail === affiliate.email;
              return (
                <Fragment key={affiliate.email}>
                  <tr className="border-b border-[rgb(var(--border))] last:border-0">
                    <td className="px-4 py-3 font-medium">{affiliate.name}</td>
                    <td className="px-4 py-3">{affiliate.email}</td>
                    <td className="px-4 py-3 font-mono text-xs">{affiliate.couponCodes.join(', ')}</td>
                    <td className="px-4 py-3 font-mono text-xs">{affiliate.pixKey ?? 'N/D'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {payments ? formatPrimaryCurrency(payments.byCurrency, 'pending') : 'N/D'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {payments ? formatPrimaryCurrency(payments.byCurrency, 'paid') : 'N/D'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <button type="button" className="btn-secondary" onClick={() => toggleForm(affiliate.email)}>
                        {formOpen ? 'Cancelar' : 'Registrar pagamento'}
                      </button>
                    </td>
                  </tr>
                  {formOpen ? (
                    <tr className="border-b border-[rgb(var(--border))] last:border-0">
                      <td colSpan={7} className="bg-[rgb(var(--bg))] px-4 py-4">
                        <div className="flex flex-wrap items-end gap-3">
                          <label className="flex flex-col gap-1 text-xs text-[rgb(var(--text-muted))]">
                            Valor
                            <input
                              type="text"
                              inputMode="decimal"
                              className="input w-28"
                              placeholder="0,00"
                              value={form.amount}
                              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
                            />
                          </label>
                          <label className="flex flex-col gap-1 text-xs text-[rgb(var(--text-muted))]">
                            Moeda
                            <select
                              className="input w-24"
                              value={form.currency}
                              onChange={(e) => setForm((prev) => ({ ...prev, currency: e.target.value }))}
                            >
                              <option value="BRL">BRL</option>
                              <option value="USD">USD</option>
                            </select>
                          </label>
                          <label className="flex flex-col gap-1 text-xs text-[rgb(var(--text-muted))]">
                            Data do pagamento
                            <input
                              type="date"
                              className="input"
                              value={form.paidAt}
                              onChange={(e) => setForm((prev) => ({ ...prev, paidAt: e.target.value }))}
                            />
                          </label>
                          <label className="flex flex-1 min-w-[160px] flex-col gap-1 text-xs text-[rgb(var(--text-muted))]">
                            Nota (opcional)
                            <input
                              type="text"
                              className="input"
                              placeholder="Ex.: Pix"
                              value={form.note}
                              onChange={(e) => setForm((prev) => ({ ...prev, note: e.target.value }))}
                            />
                          </label>
                          <button
                            type="button"
                            className="btn-primary"
                            disabled={submitting}
                            onClick={() => handleSubmitPayment(affiliate.email)}
                          >
                            {submitting ? 'Salvando…' : 'Confirmar pagamento'}
                          </button>
                        </div>
                        {formError ? <p className="mt-2 text-sm text-error">{formError}</p> : null}
                        {payments && payments.payments.length > 0 ? (
                          <div className="mt-4 border-t border-[rgb(var(--border))] pt-3">
                            <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[rgb(var(--text-muted))]">
                              Pagamentos já registrados
                            </p>
                            <ul className="space-y-1 text-sm">
                              {payments.payments.map((payment) => (
                                <li key={payment.id} className="flex items-center justify-between gap-3">
                                  <span className="text-[rgb(var(--text-muted))]">
                                    {formatDateTime(payment.paidAt)}
                                    {payment.note ? ` · ${payment.note}` : ''}
                                  </span>
                                  <span className="font-mono">{formatCurrency(payment.amount, payment.currency)}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              );
            })}
            {!loading && affiliates.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-[rgb(var(--text-muted))]">
                  Nenhum afiliado na planilha ainda.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
